import axios from "axios";
import catalog from "@/config/menu-permissions.json";
const flatten = (nodes) =>
  nodes.flatMap((node) =>
    node.type === "page" ? [node] : flatten(node.children),
  );
export const managedPages = flatten(catalog.sections);
const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
export const accessPolicy = (user) => {
  const policy = user?.accessPolicy || user?.data?.accessPolicy;
  return policy?.mode === "enforce" ? policy : null;
};
export function actionAllowed(user, page, action = "view", legacy = false) {
  const policy = accessPolicy(user);
  if (!policy) return legacy;
  if (user.accessUnavailable) return false;
  if (action !== "view" && user?.data?.readOnly) return false;
  const state = policy.pages?.[page];
  return Boolean(
    state?.view && (action === "view" || state.actions?.[action] === true),
  );
}
export function pageForCandidates(candidates) {
  const names = candidates.map(normalize);
  const pathPages = {
    role: "roles",
    user: "users",
    menuItems: "menu-items",
    trainingCenter: "provider",
    accreditor: "accreditor",
    generalTimes: "config",
    preservice: "preservice-dashboard",
    preService: "tvet",
    educationalPrograms: "education",
    catIIRequirement: "cat-ii",
    cpdActivityCategory: "activity-categories",
    healthProfessions: "health-professions",
    trainerSelectionCriteria: "trainer-criteria",
    traineeFacilities: "trainee-facilities",
    levelOfStudies: "study-levels",
    userFacility: "facilities",
  };
  const path = Object.entries(pathPages).find(([name]) =>
    names.includes(normalize(name)),
  );
  if (path) return path[1];
  const program = {
    tvetprogram: "tvet",
    genericentry: "generic-entry",
    postbasic: "post-basic",
    postgraduate: "post-graduate",
    graduateentry: "graduate-entry",
  };
  for (const [name, id] of Object.entries(program))
    if (
      names.some(
        (value) => value === name || value.endsWith("preservice" + name),
      )
    )
      return id;
  return managedPages.find((page) =>
    [page.id, page.label, page.path, ...page.menuNames].some((name) =>
      names.includes(normalize(name)),
    ),
  )?.id;
}
export function projectLegacyPermissions(user, policy) {
  const source =
    user.permissions || user.data?.userRoles?.role_menu_permissions || [];
  const existingNames = new Set(
    source.map((permission) => normalize(permission.menuItem?.menuItemName)),
  );
  const missing = managedPages.flatMap((page) =>
    page.menuNames
      .filter((name) => !existingNames.has(normalize(name)))
      .map((name) => ({ menuItem: { menuItemName: name } })),
  );
  const projected = [...source, ...missing].map((permission) => {
    const page = pageForCandidates([permission.menuItem?.menuItemName]);
    if (!page)
      return {
        ...permission,
        canRead: false,
        canCreate: false,
        canUpdate: false,
        canReadOthers: false,
        canAccreditCourse: false,
      };
    const menu = normalize(permission.menuItem?.menuItemName);
    const create =
      page === "roles"
        ? menu === "usergroups"
          ? "createGroup"
          : "createRole"
        : "create";
    const edit =
      page === "roles"
        ? menu === "permissions"
          ? "changePermissions"
          : menu === "usergroups"
            ? "editGroup"
            : "editRole"
        : "edit";
    return {
      ...permission,
      canRead: actionAllowed(user, page),
      canCreate: actionAllowed(user, page, create),
      canUpdate: actionAllowed(user, page, edit),
      canDelete: actionAllowed(user, page, "delete"),
      canAccreditCourse: actionAllowed(user, page, "accredit"),
      canReadOthers: policy.pages?.[page]?.scope === "others",
    };
  });
  user.permissions = projected;
  if (user.data?.userRoles)
    user.data.userRoles.role_menu_permissions = projected;
}
let pending;
export async function refreshAccess() {
  const raw = localStorage.getItem("user");
  if (!raw) return;
  if (pending) return pending;
  pending = (async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      const { data } = await axios.get("/access/me");
      user.accessPolicy = data;
      user.accessUnavailable = false;
      if (data.mode === "enforce") projectLegacyPermissions(user, data);
    } catch (error) {
      if (error.response?.status === 404 && !accessPolicy(user)) return;
      user.accessUnavailable = true;
      localStorage.setItem("user", JSON.stringify(user));
      throw error;
    }
    localStorage.setItem("user", JSON.stringify(user));
  })().finally(() => {
    pending = null;
  });
  return pending;
}
